import { useCallback, useEffect, useRef } from "react";
import "../../GamePage.scss";
import { useGameStore } from "../../stores/gameStore";

interface MinimapProps {
  size: number;
}

const Minimap = ({ size }: MinimapProps) => {
  const { storedTreeData, playerPosition, entityPosition, scrollPosition } =
    useGameStore((state: any) => ({
      storedTreeData: state.storedTreeData,
      playerPosition: state.playerPosition,
      entityPosition: state.entityPosition,
      scrollPosition: state.scrollPosition,
    }));
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastTimeRef = useRef<number | null>(null);
  const height = 100;
  const width = 100;

  const draw = useCallback(
    (context: CanvasRenderingContext2D) => {
      context.clearRect(0, 0, context.canvas.width, context.canvas.height);
      context.save();
      context.translate((width / 2) * size, (height / 2) * size);
      context.rotate(Math.PI);
      context.translate((-width / 2) * size, (-height / 2) * size);

      // Draw the rectangle representing the minimap background
      context.fillStyle = "#6e9174";
      context.strokeRect(0, 0, width * size, height * size);
      context.fillRect(0, 0, width * size, height * size);

      // Draw trees
      context.fillStyle = "#426a49";
      if (storedTreeData) {
        storedTreeData.forEach((tree: any) => {
          context.beginPath();
          context.arc(
            (width / 2 + tree[0].x) * size,
            (height / 2 + tree[0].z) * size,
            3,
            0,
            2 * Math.PI
          );
          context.fill();
        });
      }

      // Draw entity
      context.fillStyle = "red";
      if (entityPosition) {
        entityPosition.forEach((entity: any) => {
          context.beginPath();
          context.arc(
            (width / 2 + entity.x) * size,
            (height / 2 + entity.z) * size,
            4,
            0,
            2 * Math.PI
          );
          context.fill();
        });
      }

      // Draw scroll
      context.fillStyle = "aqua";
      if (scrollPosition) {
        scrollPosition.forEach((scroll: any) => {
          context.beginPath();
          context.arc(
            (width / 2 + scroll.x) * size,
            (height / 2 + scroll.z) * size,
            4,
            0,
            2 * Math.PI
          );
          context.fill();
        });
      }

      // Draw player
      context.fillStyle = "gold";
      if (playerPosition) {
        context.beginPath();
        context.arc(
          (width / 2 + playerPosition[0]?.x) * size,
          (height / 2 + playerPosition[0]?.z) * size,
          4,
          0,
          2 * Math.PI
        );
        context.fill();
      }

      context.restore();
    },
    [size, storedTreeData, entityPosition, scrollPosition, playerPosition]
  );

  const animate = useCallback(
    (time: number) => {
      const canvas = canvasRef.current;
      if (canvas) {
        const context = canvas.getContext("2d");
        if (context) {
          if (lastTimeRef.current !== null) {
            draw(context);
          }
          lastTimeRef.current = time;
          requestAnimationFrame(animate);
        }
      }
    },
    [draw]
  );

  useEffect(() => {
    requestAnimationFrame(animate);
  }, [animate]);

  return (
    <div className="minimap-container">
      <canvas
        ref={canvasRef}
        width={width * size}
        height={height * size}
        className="minimap"
      />
    </div>
  );
};

export default Minimap;
